import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { api, apiError } from "@/lib/api";
import { PromptIcon } from "@/components/PromptList";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedPrompt() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/shared/prompts/${shareId}`)
      .then(({ data }) => setPrompt(data))
      .catch(() => setMissing(true));
  }, [shareId]);

  const keepIt = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/prompts/custom/from-share/${shareId}`);
      toast.success(`${data.label} is now one of your prompts`);
      navigate(`/chat?prompt=${data.id}`);
    } catch (e) {
      if (e.response?.status === 401) navigate("/signup");
      else toast.error(apiError(e.response?.data?.detail) || "Could not keep that prompt");
    } finally {
      setSaving(false);
    }
  };

  if (missing) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="shared-prompt-missing">
        <p className="text-sm text-ink-soft">This prompt link is no longer available.</p>
        <Link to="/" className="mt-6 inline-flex text-sm text-brand hover:underline">Go to ChatMiLu</Link>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24" data-testid="shared-prompt-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  const isMine = String(prompt.prompt_key || "").startsWith("mine:");
  const openKey = String(prompt.prompt_key || "").replace(/^tool:/, "");

  return (
    <div className="mx-auto max-w-xl px-5 py-16 sm:px-8" data-testid="shared-prompt-page">
      <Link to="/" data-testid="shared-prompt-logo">
        <img src="/logo.png" alt="ChatMiLu" className="h-10 w-auto" />
      </Link>

      <p className="mt-10 text-xs uppercase tracking-[0.2em] text-ink-soft" data-testid="shared-prompt-by">
        {prompt.shared_by} shared a prompt with you
      </p>

      <div className="mt-4 flex items-center gap-4 rounded-3xl border border-white/10 bg-panel p-5">
        {prompt.image ? (
          <img
            data-testid="shared-prompt-image"
            src={prompt.image}
            alt={prompt.label}
            className="h-20 w-20 shrink-0 rounded-2xl object-cover"
          />
        ) : (
          <span className="grid h-20 w-20 shrink-0 place-items-center rounded-2xl border border-white/10 bg-page">
            <PromptIcon name={prompt.icon || "Sparkles"} className="h-8 w-8 text-brand" />
          </span>
        )}
        <div className="min-w-0">
          <h1 data-testid="shared-prompt-label" className="font-display text-xl font-extrabold tracking-tighter sm:text-2xl">
            {prompt.label}
          </h1>
          <p className="mt-1 text-sm text-ink-soft">{prompt.blurb}</p>
        </div>
      </div>

      {prompt.ask && (
        <p className="mt-5 text-sm text-ink-soft" data-testid="shared-prompt-ask">
          It will ask you: “{prompt.ask}”
        </p>
      )}

      {isMine ? (
        <button
          data-testid="shared-prompt-save"
          onClick={keepIt}
          disabled={saving}
          className="mt-7 inline-flex min-h-[48px] items-center rounded-full bg-brand px-7 text-sm text-white hover:bg-brand-hover active:scale-95"
        >
          {saving ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Sparkles className="me-2 h-4 w-4" />}
          Keep it and use it <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
        </button>
      ) : (
        <Link
          to={`/chat?prompt=${openKey}`}
          data-testid="shared-prompt-open"
          className="mt-7 inline-flex min-h-[48px] items-center rounded-full bg-brand px-7 text-sm text-white hover:bg-brand-hover"
        >
          <Sparkles className="me-2 h-4 w-4" /> Try it in ChatMiLu <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
        </Link>
      )}
      <p className="mt-4 text-xs text-ink-soft">
        Free to try — chat in 50 languages, then explore every other prompt.
      </p>
    </div>
  );
}
